/**
 * KAN-1641 — potvrzení zrušení objednávky s důvodem.
 *
 * Jeden dialog pro admin („Zrušit objednávku") i zákazníka („Zrušit
 * objednávku" / „Požádat o zrušení"). Důvody přicházejí z číselníku na
 * serveru už přeložené — dialog žádný seznam nezná. Texty dodává volající
 * ze svého namespace (dialog je namespace-agnostický jako ``useConfirm``).
 */

import { useState, type JSX, type ReactNode } from "react";

import type { OrderCancelReason } from "@/api/types";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

export interface CancelOrderDialogProps {
  title: string;
  description: string;
  reasonLabel: string;
  notePlaceholder: string;
  confirmLabel: string;
  cancelLabel: string;
  /** Hláška, když se potvrdí bez vybraného důvodu. */
  reasonRequired: string;
  reasons: readonly OrderCancelReason[];
  busy: boolean;
  /** Přeložená chyba serveru, nebo ``null``. */
  error: string | null;
  onConfirm: (reason: string, note: string) => void;
  onClose: () => void;
  testId: string;
  /** Doplňková otázka volajícího (KAN-1738: co s hmotou po výrobě). */
  extra?: ReactNode;
  /** Nevyplněná doplňková otázka — hláška, která potvrzení zablokuje. */
  extraMissing?: string | null;
}

export function CancelOrderDialog({
  title,
  description,
  reasonLabel,
  notePlaceholder,
  confirmLabel,
  cancelLabel,
  reasonRequired,
  reasons,
  busy,
  error,
  onConfirm,
  onClose,
  testId,
  extra,
  extraMissing = null,
}: CancelOrderDialogProps): JSX.Element {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [missing, setMissing] = useState(false);
  const [showExtraMissing, setShowExtraMissing] = useState(false);

  return (
    <ConfirmDialog
      open
      title={title}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      busy={busy}
      testId={testId}
      onClose={onClose}
      onConfirm={() => {
        if (!reason) {
          setMissing(true);
          return;
        }
        if (extraMissing) {
          setShowExtraMissing(true);
          return;
        }
        onConfirm(reason, note.trim());
      }}
      description={
        <div className="space-y-3">
          <p>{description}</p>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">{reasonLabel}</span>
            <select
              className="select w-full rounded-md border border-border bg-surface px-2 py-1.5"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setMissing(false);
              }}
              data-testid={`${testId}-reason`}
            >
              <option value="" disabled>
                —
              </option>
              {reasons.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <textarea
            className="input w-full rounded-md border border-border bg-surface px-2 py-1.5"
            rows={3}
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={notePlaceholder}
            data-testid={`${testId}-note`}
          />
          {extra}
          {(missing || (showExtraMissing && extraMissing) || error) && (
            <p className="text-sm" style={{ color: "var(--danger)" }} role="alert">
              {missing ? reasonRequired : showExtraMissing && extraMissing ? extraMissing : error}
            </p>
          )}
        </div>
      }
    />
  );
}
